import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Grid, Card, CardContent, 
  CardMedia, Pagination, TextField, InputAdornment, 
  CircularProgress, Divider, Chip, Alert
} from '@mui/material';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TelegramIcon from '@mui/icons-material/Telegram';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import SEO from '../components/common/SEO';
import { getCanonicalUrl } from '../utils/seoConstants';
import StructuredData from '../components/common/StructuredData';

// Маппинг английских названий категорий на русские
const CATEGORY_LOCALIZATION = {
  'sauna': 'Баня',
  'health': 'Здоровье',
  'rent': 'Аренда',
  'outdoor': 'Отдых на природе',
  'maintenance': 'Обслуживание домика',
  'cooking': 'Готовка и кухня',
  'cleaning': 'Уборка',
  'heating': 'Отопление',
  'garden': 'Сад и огород',
  'entertainment': 'Развлечения',
  'security': 'Безопасность',
  'other': 'Другое'
};

// Эмодзи для категорий
const CATEGORY_EMOJIS = {
  'Баня': '🛁',
  'Здоровье': '💊',
  'Аренда': '🏠',
  'Отдых на природе': '🌲',
  'Обслуживание домика': '🔧',
  'Готовка и кухня': '👨‍🍳',
  'Уборка': '🧹',
  'Отопление': '🔥',
  'Сад и огород': '🌱',
  'Развлечения': '🎮',
  'Безопасность': '🛡️',
  'Другое': '📝'
};

// Список всех категорий для фильтра
const ALL_CATEGORIES = [
  'sauna', 'health', 'rent', 'outdoor', 'maintenance', 'cooking', 
  'cleaning', 'heating', 'garden', 'entertainment', 'security', 'other'
];

const TipsPage = () => {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Функция для локализации категории
  const localizeCategory = (category) => {
    if (!category) return '';
    return CATEGORY_LOCALIZATION[category] || category;
  };
  
  // Функция для получения эмодзи категории
  const getCategoryEmoji = (category) => {
    const localizedCategory = localizeCategory(category);
    return CATEGORY_EMOJIS[localizedCategory] || '📝';
  };

  // Fetch tips
  const fetchTips = async (pageNum = 1, searchQuery = '', categoryFilter = null) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/tips', {
        params: {
          page: pageNum,
          limit: 9,
          search: searchQuery,
          status: 'published',
          category: categoryFilter
        }
      });
      
      // Данные успешно получены
      setTips(response.data.tips);
      setTotalPages(response.data.totalPages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tips:', err);
      setError('Не удалось загрузить полезные советы. Пожалуйста, попробуйте позже.');
      setLoading(false);
    }
  };
  
  // Загрузка категорий
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      // Можно использовать имеющийся список категорий или загрузить с сервера
      // В данном случае используем предопределенный список
      setCategories(ALL_CATEGORIES);
      setLoadingCategories(false);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setLoadingCategories(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCategories();
    fetchTips();
  }, []);

  // Handle search
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for search
    const newTimeout = setTimeout(() => {
      setPage(1);
      fetchTips(1, value, selectedCategory);
    }, 500);
    
    setSearchTimeout(newTimeout);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchTips(value, search, selectedCategory);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Обработчик выбора категории
  const handleCategoryClick = (category) => {
    // Если категория уже выбрана, то снимаем выбор
    const newCategory = selectedCategory === category ? null : category;
    setSelectedCategory(newCategory);
    setPage(1);
    fetchTips(1, search, newCategory);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'd MMMM yyyy', { locale: ru });
  };

  // Truncate text
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* SEO метаданные */}
      <SEO 
        title="Полезные советы по недвижимости и отдыху | DomikTyumen"
        description="Практические советы по уходу за домом, баней, организации отдыха и жизни в загородном доме. Экспертные рекомендации от DomikTyumen."
        keywords="полезные советы, дом, баня, отдых, уход, недвижимость, Тюмень"
        canonicalUrl={getCanonicalUrl('/tips')}
      />
      
      {/* Структурированные данные для поисковых систем */}
      <StructuredData 
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "headline": "Полезные советы по недвижимости и отдыху",
          "description": "Практические советы по уходу за домом, баней, организации отдыха и жизни в загородном доме",
          "publisher": {
            "@type": "Organization",
            "name": "DomikTyumen",
            "logo": {
              "@type": "ImageObject",
              "url": `${window.location.origin}/logo.png`
            }
          }
        }} 
      />
      
      {/* Заголовок и поиск */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LightbulbIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Полезные советы
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Поиск советов..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>
        
        {/* Блок с информацией о телеграм-боте */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Alert 
            severity="info" 
            icon={<TelegramIcon fontSize="large" />}
            sx={{ 
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: '800px',
              '& .MuiAlert-icon': {
                color: '#0088cc', // Цвет Telegram
                fontSize: '2rem',
                alignSelf: 'center'
              }
            }}
          >
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Все полезные советы доступны в нашем Telegram-боте!
              </Typography>
              <Typography variant="body2">
                Подписывайтесь на <a href="https://t.me/DomikTyumenBot" target="_blank" rel="noopener" style={{ fontWeight: 'bold', color: '#0088cc' }}>@DomikTyumenBot</a> и получайте советы прямо в мессенджере.
              </Typography>
            </Box>
          </Alert>
        </Box>
        
        {/* Фильтр по категориям */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
            Фильтр по категориям:
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1,
            mt: 1
          }}>
            {loadingCategories ? (
              <CircularProgress size={24} />
            ) : (
              categories.map((category) => (
                <Chip
                  key={category}
                  label={`${getCategoryEmoji(category)} ${localizeCategory(category)}`}
                  onClick={() => handleCategoryClick(category)}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                  sx={{ 
                    mb: 1,
                    '&:hover': {
                      backgroundColor: selectedCategory === category ? 'primary.main' : 'rgba(0, 0, 0, 0.08)',
                    }
                  }}
                />
              ))
            )}
            {selectedCategory && (
              <Chip
                label="Сбросить фильтр"
                onClick={() => handleCategoryClick(selectedCategory)}
                onDelete={() => handleCategoryClick(selectedCategory)}
                color="secondary"
                sx={{ mb: 1 }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Контент */}
      {loading ? (
        <Box sx={{ textAlign: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', my: 8 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : tips.length === 0 ? (
        <Box sx={{ textAlign: 'center', my: 8 }}>
          <Typography variant="h6">
            {search ? 'По вашему запросу ничего не найдено' : 'Советы пока не опубликованы'}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={4}>
            {tips.map((tip) => (
              <Grid item xs={12} sm={6} md={4} key={tip.id}>
                <Card 
                  component={Link} 
                  to={`/tips/${tip.slug}`} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    textDecoration: 'none',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={tip.featuredImage || '/assets/stanleydapps_logo.png'}
                    alt={tip.title}
                    onError={(e) => {
                      e.target.src = '/assets/stanleydapps_logo.png';
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
                      {tip.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'justify' }}>
                      {truncateText(tip.excerpt, 120)}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(tip.publishedAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <VisibilityIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {tip.viewCount}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Пагинация */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default TipsPage;
